<template>
    <div :id="modalID" class="modal fade" data-bs-backdrop="static" data-bs-keyboard="true" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document" style="min-width: 800px">
            <div class="modal-content">
                <div class="modal-header d-flex align-items-center">
                    <div style="font-size: 15px; font-weight: 500; line-height: 21px; color: #00203f">{{ $t('views.inventory.modals.addProduct.title') }}</div>
                    <button type="button" class="close-button btn btn-link" @click="closeModal"><i class="bi bi-x-lg" style="color: #677788"></i></button>
                </div>
                <div class="modal-body">
                    <div style="margin-bottom: 10px">
                        <label style="font-size: 14px; font-weight: 400; line-height: 21px; color: #00203f; margin-bottom: 10px">{{ $t('views.inventory.modals.addProduct.asin.label') }}</label>
                        <input v-model="form.asin" type="text" class="form-control" :placeholder="$t('views.inventory.modals.addProduct.asin.placeholder')" @focus="() => (asinCopy = form.asin)" @blur="handleAsin" />
                    </div>
                    <div style="margin-bottom: 20px">
                        <label class="d-flex align-items-center">
                            <input v-model="form.isGetDataFromMarket" type="checkbox" class="sm-input form-check-input mt-0" style="margin-right: 8px" />
                            <span>{{ $t('views.inventory.modals.addProduct.confirmText') }}</span>
                        </label>
                    </div>
                    <div v-if="isLoadingAsinDetail" class="d-flex align-items-center" style="gap: 5px">
                        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <span>{{ $t('components.datatable.index.loadingText') }}</span>
                    </div>

                    <div v-else class="d-flex align-items-center" style="gap: 20px">
                        <div style="width: 100%">
                            <label>{{ $t('views.inventory.modals.addProduct.cog.label') }}</label>
                            <div class="input-group">
                                <CurrencyInput
                                    v-model="form.price"
                                    :options="{ valueRange: { min: 1, max: undefined } }"
                                    :placeholder="$t('views.inventory.modals.addProduct.cog.placeholder')"
                                    class="sm-input form-control"
                                    :disabled="form.isGetDataFromMarket"
                                />
                                <span class="input-group-text" :style="!form.isGetDataFromMarket ? 'background-color: #fff !important' : ''">$</span>
                            </div>
                        </div>
                        <div style="width: 100%">
                            <label>{{ $t('views.inventory.modals.addProduct.sku.label') }}</label>
                            <input v-model="form.sku" type="text" :placeholder="$t('views.inventory.modals.addProduct.sku.placeholder')" class="sm-input form-control" :disabled="form.isGetDataFromMarket" @keyup="slugify" />
                        </div>
                    </div>
                    <div v-if="isShowAlert && !isLoadingAsinDetail" class="alert alert-soft-warning text-primary" role="alert" style="margin-top: 10px">{{ $t('views.inventory.modals.addProduct.alertText') }}</div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-primary btn-sm d-flex align-items-center" :disabled="isSubmitButtonDisabled || isLoading" @click="submit" style="gap: 5px">
                        <div v-if="isLoading" class="spinner-border spinner-border-sm" role="status"></div>
                        <span>{{ $t('views.inventory.modals.addProduct.submitButton') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { Modal } from 'bootstrap'
import { countryInfo } from '@/utils/helper'
import InventoryService from '@/services/inventory'
import CurrencyInput from '@/components/CurrencyInput.vue'

const countries = countryInfo()

export default {
    name: 'AddProductModal',
    components: {
        CurrencyInput,
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            countries,
            modalID: 'AddProductModal-' + crypto.randomUUID(),
            modalInstance: null,
            isLoading: false,
            isLoadingAsinDetail: false,
            form: {
                asin: null,
                sku: null,
                price: null,
                isGetDataFromMarket: true,
            },
            isShowAlert: false,
        }
    },
    computed: {
        ...mapState('inventory', ['list', 'selectedRows']),
        ...mapGetters('market', ['currentMarket']),
        isSubmitButtonDisabled() {
            return false
        },
        getAmazonCountryURL() {
            return countries[this.currentMarket.short].domain
        },
    },
    methods: {
        ...mapActions('inventory', ['addToInventory']),
        closeModal() {
            this.modalInstance.hide()
            this.$emit('update:modelValue', false)
        },
        handleAsin() {
            if (this.asinCopy !== this.form.asin) {
                this.isLoadingAsinDetail = true
                if (/^B[A-Z0-9]{9}$/.test(this.form.asin)) {
                    InventoryService.addToInventoryByAsin(this.form.asin)
                        .then(({ data }) => {
                            this.form.price = data.price ? this.formatNumber(data.price) : null
                            this.form.sku = data.sku
                            this.form.isGetDataFromMarket = true
                            this.isShowAlert = ['add_inventory_with_min_current_cost_calculation', 'add_inventory_with_max_current_cost_calculation'].includes(data.price_option) && !data.price
                        })
                        .catch(() => {})
                        .finally(() => {
                            if (!this.form.price) {
                                this.form.isGetDataFromMarket = false
                            }
                            this.isLoadingAsinDetail = false
                        })
                } else {
                    this.isLoadingAsinDetail = false
                }
            }
        },
        submit() {
            this.isLoading = true
            this.addToInventory(this.form)
                .then(() => {
                    this.form = { asin: null, sku: null, price: null, isGetDataFromMarket: true }
                })
                .catch(() => {})
                .finally(() => {
                    this.isLoading = false
                })
        },
        slugify(event) {
            const replacedText = event.target.value.replace(/ı/g, 'i').replace(/İ/g, 'i')
            const normalizedText = replacedText.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            this.form.sku = normalizedText
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .trim('-')
        },
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
    },
    watch: {
        modelValue: function (data) {
            if (data) {
                const modalEl = document.getElementById(this.modalID)
                this.modalInstance = new Modal(modalEl, { backdrop: true })
                this.modalInstance.show()
                modalEl.addEventListener('hidden.bs.modal', () => {
                    this.closeModal()
                })
            } else {
                this.closeModal()
            }
        },
        'form.isGetDataFromMarket': function (data) {
            if (data) {
                this.handleAsin()
            }
        },
    },
}
</script>

<style lang="scss" scoped>
[id^='AddProductModal-'] {
    .close-button {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;

        &:hover {
            background-color: #f8fafd;
        }
    }

    label {
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        margin-bottom: 10px;
        color: #677788;
    }

    input[type='checkbox']:not(:checked) {
        border: 1px solid #677788;
    }
}
</style>
